import React, { useState, useEffect, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link, useLocation } from "react-router-dom";
import { auth, db } from "../firebase";
import { doc, getDoc, getDocs, query, collection, where } from "firebase/firestore";
import "./../App.css";

// Create a motion-enabled Link component
const MotionLink = motion(Link);

function Menu() {
    const location = useLocation();
    const [username, setUsername] = useState("Guest");
    const [userData, setUserData] = useState(null);
    const [propertyStatus, setPropertyStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [propertyName, setPropertyName] = useState("");
    const [propertyLogo, setPropertyLogo] = useState("");
    const [documentStatus, setDocumentStatus] = useState(null);

    const [openMenu, setOpenMenu] = useState(null);

    // Color Palette
    const colors = {
        darkGreen: '#06362D',
        calPolyGreen: '#155332',
        vanilla: '#DEE2B1',
        gold: '#FFD500',
        goldenrod: '#D1A505'
    };

    useEffect(() => {
        const unsubscribe = auth.onAuthStateChanged((user) => setUserData(user));
        return () => unsubscribe();
    }, []);

    useEffect(() => {
        const fetchUserRole = async () => {
            try {
                if (!userData) return;
                const userRef = doc(db, "authroles", userData.uid);
                const userSnap = await getDoc(userRef);

                if (userSnap.exists()) {
                    const user = userSnap.data();
                    setUsername(user.name || "Guest");
                    
                    if (user.propertyId) {
                        fetchPropertyAndDocuments(user.propertyId);
                    } else {
                        setDocumentStatus("pending");
                        setPropertyStatus("no-property");
                    }
                }
                setLoading(false);
            } catch (error) {
                console.error("Error fetching user role:", error);
            }
        };
        fetchUserRole();
    }, [userData]);

    const fetchPropertyAndDocuments = async (propertyId) => {
        try {
            const propRef = doc(db, "properties", propertyId);
            const propSnap = await getDoc(propRef);

            if (propSnap.exists()) {
                const propData = propSnap.data();
                setPropertyName(propData.hotelname || "");
                setPropertyLogo(propData.logo || "");

                if (propData.status === "Deactivate") {
                    window.location.href = "/disabled-property";
                    return;
                }
                setPropertyStatus(propData.status || null);
            }

            const docsQuery = query(collection(db, "documents"), where("propertyId", "==", propertyId));
            const docsSnap = await getDocs(docsQuery);

            if (!docsSnap.empty) {
                const docsData = docsSnap.docs[0].data();
                setDocumentStatus(docsData.status);
            } else {
                setDocumentStatus("pending");
            }
        } catch (err) {
            console.error("Error fetching property/documents:", err);
            setDocumentStatus("pending");
        } finally {
            setLoading(false);
        }
    };

    // ------------------------------
    //   EXACT MENU STRUCTURE REQUESTED
    // ------------------------------
    const fullMenu = useMemo(() => [
        { icon: "fas fa-home", text: "Home", link: "/home" },
        {
            icon: "fas fa-calendar-check",
            text: "Reservations",
            submenu: [
                { text: "Reserve Room", link: "/reservation" },
                { text: "View Reservations", link: "/view-reservations" },
                { text: "Checkouts", link: "/checkouts" },
                // { text: "Damage Notes", link: "/damage-notes" },
                { text: "Pending Reservations", link: "/pending-reservations" },
            ]
        },
         {
            icon: "fas fa-table",
            text: "CM Connectivity",
            submenu: [
                { text: "Inventory Updates", link: "/inventory-updates" },
                { text: "Rate Updates", link: "/rate-updates" },
                { text: "OTA Info", link: "/ota-info" },
                { text: "Rooms List", link: "/room-list" },
                { text: "Channel Manager Connectivity", link: "/channel-manager-connectivity" },
            ]
        },
        {
            icon: "fas fa-boxes",
            text: "Cleaning",
            submenu: [
                { text: "Cleaning Rooms", link: "/cleaning-rooms" },
                { text: "Cleaned Rooms", link: "/cleaned-rooms" },
            ]
        },
        {
            icon: "fas fa-file",
            text: "Report",
            submenu: [
                { text: "Self Report", link: "/self-report" },
                // { text: "All Daily Report", link: "/all-daily-report" },
                // { text: "Datewise Future Bookings", link: "/datwise-future-bokoings" },
                // { text: "Roomtype Wise Future Bookings", link: "/roomtypewise-future-bokoings" },
                { text: "Checkout Alert", link: "/checkout-alert" },
            ]
        },
        // { icon: "fas fa-life-ring", text: "Help & Support", link: "/help-support" }
    ], []);

    const menuToShow = fullMenu;

    // Automatically open submenu on load based on current route
    useEffect(() => {
        const currentPath = location.pathname;
        let activeIndex = null;

        menuToShow.forEach((item, index) => {
            if (item.submenu) {
                const isActive = item.submenu.some(sub => sub.link === currentPath);
                if (isActive) activeIndex = index;
            } else if (item.link === currentPath) {
                activeIndex = index;
            }
        });

        if (activeIndex !== null) {
            setOpenMenu(activeIndex);
        }
    }, [location.pathname, menuToShow]);

    const firstLetter = username.charAt(0).toUpperCase();

    return (
        <aside style={styles.sidebar}>
            {/* TOP PROFILE / PROPERTY HEADER */}
            <div style={styles.profileArea}>
                {propertyLogo ? (
                    <img
                        src={propertyLogo}
                        alt="Property Logo"
                        style={styles.logoImg}
                    />
                ) : (
                    <div style={styles.fallbackAvatar}>
                        {firstLetter}
                    </div>
                )}
                <h5 style={styles.usernameText}>{username}</h5>
                {propertyName && (
                    <p style={styles.propertyNameText}>
                        {propertyName}
                    </p>
                )}
            </div>

            {/* MENU AREA */}
            <div className="sidebar-scroll" style={styles.menuContainer}>
                {menuToShow.map((item, index) => {
                    const isOpen = openMenu === index;
                    const isMainActive = item.link === location.pathname || 
                        (item.submenu && item.submenu.some(sub => sub.link === location.pathname));
                    
                    const itemColor = isMainActive ? colors.gold : `rgba(222, 226, 177, 0.7)`;

                    const renderMainToggle = () => (
                        <>
                            <i className={item.icon} style={{ ...styles.iconStyle, color: itemColor }}></i>
                            <span style={{ 
                                color: itemColor, 
                                flex: 1, 
                                fontWeight: isMainActive ? "600" : "500",
                                letterSpacing: "0.3px"
                            }}>
                                {item.text}
                            </span>
                            
                            {item.submenu && (
                                <i className="fas fa-chevron-right" style={{
                                    fontSize: "10px",
                                    color: itemColor,
                                    transform: isOpen ? "rotate(90deg)" : "rotate(0deg)",
                                    transition: "transform 0.3s ease"
                                }}></i>
                            )}
                        </>
                    );

                    return (
                        <div key={index} style={{ marginBottom: "8px" }}>
                            {item.submenu ? (
                                <motion.div
                                    onClick={() => setOpenMenu(isOpen ? null : index)}
                                    style={{
                                        ...styles.navLink,
                                        ...(isMainActive ? styles.activeNavLink : {})
                                    }}
                                    whileHover={!isMainActive ? styles.hoverNavLink : {}}
                                >
                                    {renderMainToggle()}
                                </motion.div>
                            ) : (
                                <MotionLink
                                    to={item.link || "#0"}
                                    onClick={() => setOpenMenu(index)}
                                    style={{
                                        ...styles.navLink,
                                        ...(isMainActive ? styles.activeNavLink : {})
                                    }}
                                    whileHover={!isMainActive ? styles.hoverNavLink : {}}
                                >
                                    {renderMainToggle()}
                                </MotionLink>
                            )}

                            {/* Submenu Dropdown */}
                            <AnimatePresence>
                                {item.submenu && isOpen && (
                                    <motion.div
                                        initial={{ height: 0, opacity: 0 }}
                                        animate={{ height: "auto", opacity: 1 }}
                                        exit={{ height: 0, opacity: 0 }}
                                        transition={{ duration: 0.25, ease: "easeInOut" }}
                                        style={{ overflow: "hidden" }}
                                    >
                                        <div style={{ padding: "4px 0 8px 0" }}>
                                            {item.submenu.map((sub, sIndex) => {
                                                const isSubActive = location.pathname === sub.link;
                                                return (
                                                    <MotionLink
                                                        key={sIndex}
                                                        to={sub.link}
                                                        style={{
                                                            ...styles.submenuLink,
                                                            color: isSubActive ? colors.gold : "rgba(222, 226, 177, 0.7)",
                                                            fontWeight: isSubActive ? "600" : "400"
                                                        }}
                                                        whileHover={{ color: colors.gold, x: 4 }}
                                                    >
                                                        {sub.text}
                                                    </MotionLink>
                                                )
                                            })}
                                        </div>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>
                    );
                })}
            </div>
        </aside>
    );
}

const styles = {
    sidebar: {
        backgroundColor: "#06362D",
        width: "250px",
        minWidth: "250px",
        height: "100vh",
        position: "sticky",
        top: 0,
        display: "flex",
        flexDirection: "column",
        padding: "20px 16px",
        boxSizing: "border-box",
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        borderRight: "1px solid rgba(222, 226, 177, 0.1)",
        zIndex: 100,
    },
    profileArea: {
        textAlign: "center",
        borderBottom: "1px solid rgba(222, 226, 177, 0.15)",
        paddingBottom: "16px",
        marginBottom: "16px",
    },
    logoImg: {
        width: "55px",
        height: "55px",
        borderRadius: "50%",
        objectFit: "cover",
        margin: "0 auto 8px auto",
        display: "block",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)",
        background: "#fff"
    },
    fallbackAvatar: {
        width: "55px",
        height: "55px",
        borderRadius: "50%",
        backgroundColor: "rgba(255,255,255,0.15)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontSize: "24px",
        fontWeight: "700",
        margin: "0 auto 8px auto",
        color: "#fff",
        boxShadow: "0 2px 8px rgba(0,0,0,0.3)"
    },
    usernameText: {
        fontWeight: "600",
        fontSize: "15px",
        color: "#fff",
        margin: 0
    },
    propertyNameText: {
        fontSize: "12px",
        margin: "4px 0 0",
        color: "#DEE2B1",
        fontWeight: "500"
    },
    menuContainer: {
        flex: 1,
        overflowY: "auto",
        overflowX: "hidden",
        paddingRight: "4px",
    },
    navLink: {
        display: "flex",
        alignItems: "center",
        padding: "12px 14px",
        borderRadius: "12px",
        textDecoration: "none",
        fontSize: "14px",
        transition: "all 0.2s ease",
        cursor: "pointer",
        background: "transparent",
    },
    activeNavLink: {
        background: "linear-gradient(90deg, rgba(21, 83, 50, 0.8) 0%, rgba(6, 54, 45, 0) 100%)",
        boxShadow: "inset 3px 0px 0px #FFD500",
    },
    hoverNavLink: {
        background: "rgba(222, 226, 177, 0.05)",
    },
    iconStyle: {
        width: "30px",
        fontSize: "15px",
        textAlign: "left"
    },
    submenuLink: {
        display: "block",
        padding: "8px 14px 8px 44px",
        textDecoration: "none",
        fontSize: "13px",
        transition: "all 0.2s ease",
    }
};

export default Menu;